import { useDispatch } from 'react-redux'
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import unicornFxImage from '../../../../assets/images/task/unicorn.png'

import { Icon } from '@iconify/react'

// antd
import { Typography, message, Input, Tooltip, Popover, Table } from 'antd'

// slices
import {
    taskListSupport,
    taskCreateSupport,
    taskUpdateSupport,
    mainTaskList,
    mainTaskCreate,
    mainTaskUpdate,
} from '../../../taskRefactored/tasks.slice'
import {
    buildMainTaskUpdateBodyFromTask,
    getTaskCreatedSuccessMessage,
    isTaskMutationSuccess,
    resolveReferenceId,
    MAIN_TASK_LIST_SORT,
} from '../../../taskRefactored/tasks.service'
import EpicSelector, {
    getEpicDisplayOptionFromTask,
} from '../../../taskRefactored/components/epicSelector'
import SprintSelector, {
    getSprintDisplayOptionFromTask,
} from '../../../taskRefactored/components/sprintSelector'
import { createSwimlaneAction } from '../../../projects/projects.slice'

// components
import AssigneeSelectorButton from '../AssigneeSelectorButton'
import DateRangePicker from '../DateRangePicker'
import DeleteSectionModal from '../DeleteSectionModal'
import TaskContextMenu from '../TaskContextMenu'
import ListViewSkeleton from './ListViewSkeleton'
import { useTaskListKindPermission } from '../../projectPermissions'
import { filterSupportQuickFilterSubTasks } from '../supportQuickFilterSubtasks'

// styles
import styles from './ProjectTaskListView.module.css'
import { showActionNotification } from '../../../../components/notification'

const { Text } = Typography

const PENDING_LIST_TASK_PREFIX = '__pending_list_task__'
const createPendingListTaskId = () =>
    `${PENDING_LIST_TASK_PREFIX}${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 9)}`

const getTaskEpicId = (task) => resolveReferenceId(task?.epic ?? task?.epicId)

/**
 * Large projects return well over a thousand tasks. Each row carries date,
 * assignee, epic and sprint pickers, so render a window per section and let the
 * user pull in the rest.
 */
const INITIAL_VISIBLE_TASKS = 20
const VISIBLE_TASKS_STEP = 20
const getTaskSprintId = (task) =>
    resolveReferenceId(task?.sprint ?? task?.sprintId)

const insertTaskToGroup = (groups, groupId, task, placement = 'bottom') =>
    (groups || []).map((group) => {
        if (group?._id !== groupId || !Array.isArray(group?.list)) {
            return group
        }
        const list =
            placement === 'top' ? [task, ...group.list] : [...group.list, task]
        return { ...group, list }
    })

const replaceTaskInGroup = (groups, groupId, tempId, nextTask) =>
    (groups || []).map((group) => {
        if (group?._id !== groupId || !Array.isArray(group?.list)) {
            return group
        }
        const idx = group.list.findIndex((t) => t._id === tempId)
        if (idx < 0) {
            return { ...group, list: [...group.list, nextTask] }
        }
        const list = group.list.map((t, i) => {
            if (i !== idx) return t
            const resolvedTitle =
                String(nextTask?.title || '').trim() || t.title || ''
            return {
                ...t,
                ...nextTask,
                title: resolvedTitle,
                pendingCreate: false,
            }
        })
        return { ...group, list }
    })

const removeTaskFromGroup = (groups, groupId, taskId) =>
    (groups || []).map((group) =>
        group?._id === groupId && Array.isArray(group?.list)
            ? {
                  ...group,
                  list: group.list.filter((t) => t._id !== taskId),
              }
            : group
    )

const extractCreatedTaskFromResponse = (createdData) => {
    if (!createdData) return null
    const candidates = [
        createdData?.data?.task,
        createdData?.data,
        createdData?.task,
        createdData,
    ]
    for (const item of candidates) {
        if (!item || typeof item !== 'object') continue
        const id = item._id || item.id
        if (!id) continue
        return {
            ...item,
            _id: id,
            title: item.title ?? item.name ?? '',
        }
    }
    return null
}

const ProjectTaskListView = ({
    projectTaskList,
    projectTaskListStatus,
    projectId,
    onTaskClick,
    taskListKind = 'support',
    onAddTaskToFirstSwimlane,
    liveTaskTitlePatch = null,
    searchTerm = '',
    onRefreshTasks,
    listQuery = null,
    showRefreshOverlay = false,
}) => {
    const dispatch = useDispatch()
    const isMain = taskListKind === 'main'
    const { canEdit: canEditModule, canDelete: canDeleteModule } =
        useTaskListKindPermission(taskListKind)
    const swimlaneTypePayload = isMain ? { type: 'pms' } : {}
    const listViewContainerRef = useRef(null)
    const [tableScrollY, setTableScrollY] = useState(400)

    const refreshTasks = React.useCallback(
        (skip = 0) => {
            if (onRefreshTasks) {
                // Post-mutation sync: keep the list on screen, no skeleton.
                onRefreshTasks({}, { skip, silent: true })
                return
            }
            if (isMain) {
                dispatch(
                    mainTaskList({
                        projectId,
                        skip,
                        limit: 10,
                        sort: MAIN_TASK_LIST_SORT,
                    })
                )
            } else {
                dispatch(
                    taskListSupport({
                        skip,
                        limit: 10,
                        filter: { projectId },
                        listType: 'status',
                    })
                )
            }
        },
        [dispatch, isMain, onRefreshTasks, projectId]
    )

    const [createTaskStates, setCreateTaskStates] = useState({})
    const createTaskStatesRef = useRef({})
    const createTaskInFlightRef = useRef({})
    const [filteredAssignees] = useState([])
    const [localTaskList, setLocalTaskList] = useState(projectTaskList)
    const [collapsedSections, setCollapsedSections] = useState({})
    const [sectionMenuOpen, setSectionMenuOpen] = useState({})
    /** { [sectionId]: number } — how many rows that section currently renders. */
    const [visibleTaskCounts, setVisibleTaskCounts] = useState({})
    /** sectionId -> the "loading more" element at the end of that section. */
    const sectionSentinelsRef = useRef(new Map())
    const registerSectionSentinel = React.useCallback((sectionId, el) => {
        if (sectionId == null) return
        if (el) sectionSentinelsRef.current.set(sectionId, el)
        else sectionSentinelsRef.current.delete(sectionId)
    }, [])
    const [editingSectionId, setEditingSectionId] = useState(null)
    const [editingSectionName, setEditingSectionName] = useState('')
    const sectionNameInputRef = useRef(null)
    const [newTaskInputs, setNewTaskInputs] = useState({})
    const [newTaskAssigneeOpen, setNewTaskAssigneeOpen] = useState({})
    const [newTaskDateOpen, setNewTaskDateOpen] = useState({})
    const newTaskInputsRef = useRef({})
    const newTaskAssigneeOpenRef = useRef({})
    const newTaskDateOpenRef = useRef({})
    const skipNewTaskBlurCreateRef = useRef(false)
    const [deleteModalOpen, setDeleteModalOpen] = useState(false)
    const [deleteSectionData, setDeleteSectionData] = useState(null)
    const [deleteOption, setDeleteOption] = useState('keep') // 'keep' or 'delete'
    const [deleteSectionLoading, setDeleteSectionLoading] = useState(false)
    const [editingTasks, setEditingTasks] = useState({}) // { taskId: { isEditing, editedTitle, isUpdating } }
    const taskInputRefs = useRef({}) // { taskId: ref }
    const pendingUpdatesRef = useRef({}) // { taskId: { title, assigneeId, start, end, ... } }
    const updateTimers = useRef({}) // { taskId: timerId }
    const localTaskListRef = useRef(localTaskList)
    //  const [isAddingSection, setIsAddingSection] = useState(false)
    // const [newSectionName, setNewSectionName] = useState('')
    // const [isCreatingSection, setIsCreatingSection] = useState(false)
    // const addSectionInputRef = useRef(null)
    const [completingTasks, setCompletingTasks] = useState({})
    const [showCompletionCelebration, setShowCompletionCelebration] =
        useState(false)
    const [celebrationTaskId, setCelebrationTaskId] = useState(null)
    const celebrationTimeoutRef = useRef(null)
    const COMPLETION_CELEBRATION_MS = 2600

    const clearCompletionCelebration = React.useCallback(() => {
        if (celebrationTimeoutRef.current) {
            clearTimeout(celebrationTimeoutRef.current)
            celebrationTimeoutRef.current = null
        }
        setCelebrationTaskId(null)
        setShowCompletionCelebration(false)
    }, [])

    const triggerCompletionCelebration = React.useCallback((forTaskId) => {
        if (!forTaskId) return
        if (celebrationTimeoutRef.current) {
            clearTimeout(celebrationTimeoutRef.current)
            celebrationTimeoutRef.current = null
        }
        setCelebrationTaskId(forTaskId)
        setShowCompletionCelebration(true)
        celebrationTimeoutRef.current = setTimeout(() => {
            setCelebrationTaskId(null)
            setShowCompletionCelebration(false)
            celebrationTimeoutRef.current = null
        }, COMPLETION_CELEBRATION_MS)
    }, [])

    useEffect(
        () => () => {
            clearCompletionCelebration()
        },
        [clearCompletionCelebration]
    )

    useEffect(() => {
        localTaskListRef.current = localTaskList
    }, [localTaskList])

    useEffect(() => {
        createTaskStatesRef.current = createTaskStates
    }, [createTaskStates])

    // Sync localTaskList with projectTaskList
    useEffect(() => {
        setLocalTaskList(projectTaskList)
    }, [projectTaskList])

    /**
     * Start over at the first window only for a genuinely new result set —
     * landing, filter or search. Resetting on every background refresh would
     * throw away the rows the user had already pulled in.
     */
    useEffect(() => {
        if (showRefreshOverlay) setVisibleTaskCounts({})
    }, [showRefreshOverlay])

    // After search results load, expand collapsed groups so matches are visible
    useEffect(() => {
        const term = String(searchTerm || '').trim()
        if (!term) return
        if (projectTaskListStatus === 'loading') return

        const list = projectTaskList
        if (!Array.isArray(list) || list.length === 0) return

        setCollapsedSections((prev) => {
            let changed = false
            const next = { ...prev }
            for (const group of list) {
                const sectionId = group?._id
                // Expand any group that still has search hits
                if (
                    sectionId != null &&
                    Array.isArray(group?.list) &&
                    group.list.length > 0 &&
                    next[sectionId]
                ) {
                    next[sectionId] = false
                    changed = true
                }
            }
            return changed ? next : prev
        })
    }, [searchTerm, projectTaskList, projectTaskListStatus])

    useEffect(() => {
        if (!liveTaskTitlePatch?.taskId) return
        setLocalTaskList((prev) => {
            if (!Array.isArray(prev) || prev.length === 0) return prev
            let changed = false
            const next = prev.map((group) => {
                if (!Array.isArray(group?.list) || group.list.length === 0) {
                    return group
                }
                const updatedList = group.list.map((task) => {
                    if (task?._id !== liveTaskTitlePatch.taskId) return task
                    changed = true
                    return {
                        ...task,
                        title: liveTaskTitlePatch.title,
                    }
                })
                return changed ? { ...group, list: updatedList } : group
            })
            return changed ? next : prev
        })
    }, [liveTaskTitlePatch])

    // Ant Design Table `scroll.y` is the tbody height only; thead sits above it.
    // Measuring from viewport top to innerHeight without subtracting thead caused
    // thead + body to exceed the window so the parent (`overflow: hidden`) clipped
    // the bottom of the scrollbar. Measure space below the header row instead.
    useLayoutEffect(() => {
        const updateTableScrollHeight = () => {
            const container = listViewContainerRef.current
            if (!container) return

            const thead = container.querySelector('.ant-table-thead')
            const headerH = thead
                ? Math.ceil(thead.getBoundingClientRect().height)
                : 47

            const containerHeight = container.clientHeight
            let bodyHeight

            // Prefer flex-filled height so the table uses full main area, not only content height
            if (containerHeight > headerH + 120) {
                bodyHeight = containerHeight - headerH - 2
            } else {
                const headerBottom = thead
                    ? thead.getBoundingClientRect().bottom
                    : container.getBoundingClientRect().top + headerH
                const bottomGap = 16
                bodyHeight = window.innerHeight - headerBottom - bottomGap
            }

            setTableScrollY(Math.max(240, Math.floor(bodyHeight)))
        }

        let alive = true
        const runMeasure = () => {
            updateTableScrollHeight()
            requestAnimationFrame(() => {
                if (alive) updateTableScrollHeight()
            })
        }
        runMeasure()

        window.addEventListener('resize', updateTableScrollHeight)

        let ro
        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(() => updateTableScrollHeight())
            if (listViewContainerRef.current) {
                ro.observe(listViewContainerRef.current)
            }
        }

        return () => {
            alive = false
            window.removeEventListener('resize', updateTableScrollHeight)
            ro?.disconnect()
        }
        // `showRefreshOverlay` matters: while the skeleton is up the table (and
        // so `listViewContainerRef`) is unmounted, and this effect can only
        // measure once the real table is back on screen.
    }, [projectTaskListStatus, projectTaskList?.length, showRefreshOverlay])

    /**
     * Every section shares the one antd table body, so instead of a scroll
     * position we watch each section's end-of-list sentinel: whichever one
     * scrolls into view pulls in the next batch for that section.
     */
    useEffect(() => {
        const container = listViewContainerRef.current
        const body = container?.querySelector('.ant-table-body')
        if (!body) return undefined

        let frame = null

        const loadSectionsInView = () => {
            frame = null
            const limit = body.getBoundingClientRect().bottom + 240
            const sectionsToGrow = []
            sectionSentinelsRef.current.forEach((el, sectionId) => {
                if (!el?.isConnected) return
                if (el.getBoundingClientRect().top <= limit) {
                    sectionsToGrow.push(sectionId)
                }
            })
            if (!sectionsToGrow.length) return

            setVisibleTaskCounts((prev) => {
                const next = { ...prev }
                sectionsToGrow.forEach((sectionId) => {
                    next[sectionId] =
                        (next[sectionId] ?? INITIAL_VISIBLE_TASKS) +
                        VISIBLE_TASKS_STEP
                })
                return next
            })
        }

        const onScroll = () => {
            if (frame != null) return
            frame = requestAnimationFrame(loadSectionsInView)
        }

        body.addEventListener('scroll', onScroll, { passive: true })
        // The sentinel can already be in view on a short section.
        onScroll()

        return () => {
            body.removeEventListener('scroll', onScroll)
            if (frame != null) cancelAnimationFrame(frame)
        }
    }, [
        projectTaskListStatus,
        projectTaskList?.length,
        showRefreshOverlay,
        tableScrollY,
        visibleTaskCounts,
    ])

    const initialCreateTask = {
        open: false,
        loading: false,
        title: '',
        assigneeId: null,
        assigneeData: null,
        start: null,
        end: null,
        position: 'bottom', // 'top' or 'bottom'
        type: 'Task',
    }

    const setCreateTaskStateFor = (groupId, updater) => {
        const current =
            createTaskStatesRef.current[groupId] || initialCreateTask
        const nextValue =
            typeof updater === 'function' ? updater(current) : updater
        const next = {
            ...createTaskStatesRef.current,
            [groupId]: nextValue,
        }
        // Keep ref in sync immediately so blur/create after assignee select
        // does not read a stale assigneeId (null).
        createTaskStatesRef.current = next
        setCreateTaskStates(next)
    }

    const createTaskFromList = async (taskName, groupId) => {
        if (!canEditModule) return
        if (createTaskInFlightRef.current[groupId]) return

        const trimmedTitle = String(taskName || '').trim()
        if (!trimmedTitle) return

        const createTaskState =
            createTaskStatesRef.current[groupId] ||
            createTaskStates[groupId] ||
            initialCreateTask
        const resolvedAssigneeId = resolveReferenceId(
            createTaskState?.assigneeData || createTaskState?.assigneeId
        )
        const taskType = createTaskState?.type === 'Bug' ? 'Bug' : 'Task'
        const taskPlacement = createTaskState?.position || 'bottom'
        const isTopPlacement = taskPlacement === 'top'
        const pendingTaskId = createPendingListTaskId()

        createTaskInFlightRef.current[groupId] = true

        // Close create row immediately (same as board) and insert pending task
        setCreateTaskStateFor(groupId, initialCreateTask)
        setNewTaskInputs((prev) => ({
            ...prev,
            [groupId]: '',
        }))
        newTaskInputsRef.current = {
            ...newTaskInputsRef.current,
            [groupId]: '',
        }
        setNewTaskAssigneeOpenFor(groupId, false)
        setNewTaskDateOpenFor(groupId, false)

        setLocalTaskList((prev) => {
            const groups = prev || []
            const group = groups.find((g) => g?._id === groupId)
            const listLength = group?.list?.length ?? 0
            const pendingTask = {
                _id: pendingTaskId,
                title: trimmedTitle,
                status: groupId || 'To Do',
                type: taskType,
                position: isTopPlacement ? 0 : listLength,
                assigneeId:
                    createTaskState?.assigneeData || resolvedAssigneeId || null,
                start: createTaskState?.start || null,
                end: createTaskState?.end || null,
                pendingCreate: true,
            }
            return insertTaskToGroup(
                groups,
                groupId,
                pendingTask,
                taskPlacement
            )
        })

        const payload = isMain
            ? {
                  title: trimmedTitle,
                  projectId: projectId,
                  description: '',
                  type: taskType,
                  status: groupId || 'To Do',
                  priority: 'Medium',
                  assigneeId: resolvedAssigneeId,
                  start: createTaskState?.start || null,
                  end: createTaskState?.end || null,
                  ...(isTopPlacement ? { placement: 'top' } : {}),
              }
            : {
                  title: trimmedTitle,
                  projectId: projectId,
                  assigneeId: resolvedAssigneeId,
                  type: taskType,
                  status: groupId || 'To Do',
                  start: createTaskState?.start || null,
                  end: createTaskState?.end || null,
                  ...(isTopPlacement ? { placement: 'top' } : {}),
              }

        try {
            const result = await dispatch(
                isMain ? mainTaskCreate(payload) : taskCreateSupport(payload)
            )

            const d = result?.payload?.data
            const ok = isMain ? isTaskMutationSuccess(d) : d?.success

            if (ok) {
                const createdTask = extractCreatedTaskFromResponse(d)
                if (createdTask) {
                    setLocalTaskList((prev) =>
                        replaceTaskInGroup(
                            prev || [],
                            groupId,
                            pendingTaskId,
                            createdTask
                        )
                    )
                }

                showActionNotification({
                    type: 'success',
                    message: getTaskCreatedSuccessMessage(
                        createdTask?.type || payload?.type || 'Task'
                    ),
                    subMessage: trimmedTitle,
                    actionButtonText: 'Undo',
                    notificationKey: 'task-add',
                })

                refreshTasks(0)
            } else {
                setLocalTaskList((prev) =>
                    removeTaskFromGroup(prev || [], groupId, pendingTaskId)
                )
                setCreateTaskStateFor(groupId, {
                    ...initialCreateTask,
                    open: true,
                    title: trimmedTitle,
                    assigneeId: resolvedAssigneeId,
                    assigneeData: createTaskState?.assigneeData || null,
                    start: createTaskState?.start || null,
                    end: createTaskState?.end || null,
                    position: taskPlacement,
                    type: taskType,
                    loading: false,
                })
                setNewTaskInputs((prev) => ({
                    ...prev,
                    [groupId]: trimmedTitle,
                }))
                newTaskInputsRef.current = {
                    ...newTaskInputsRef.current,
                    [groupId]: trimmedTitle,
                }
                message.error(
                    result?.payload?.data?.message || 'Could not create task'
                )
            }
        } catch (error) {
            setLocalTaskList((prev) =>
                removeTaskFromGroup(prev || [], groupId, pendingTaskId)
            )
            setCreateTaskStateFor(groupId, {
                ...initialCreateTask,
                open: true,
                title: trimmedTitle,
                assigneeId: resolvedAssigneeId,
                assigneeData: createTaskState?.assigneeData || null,
                start: createTaskState?.start || null,
                end: createTaskState?.end || null,
                position: taskPlacement,
                type: taskType,
                loading: false,
            })
            setNewTaskInputs((prev) => ({
                ...prev,
                [groupId]: trimmedTitle,
            }))
            newTaskInputsRef.current = {
                ...newTaskInputsRef.current,
                [groupId]: trimmedTitle,
            }
            message.error('Failed to create task')
        } finally {
            delete createTaskInFlightRef.current[groupId]
        }
    }

    const handleToggleSection = (sectionId) => {
        setCollapsedSections((prev) => ({
            ...prev,
            [sectionId]: !prev[sectionId],
        }))
    }

    const handleAddTaskClick = (groupId, position = 'bottom') => {
        if (!canEditModule) return
        // Expand section if collapsed so the new-task row is visible
        setCollapsedSections((prev) => {
            if (!prev[groupId]) return prev
            return {
                ...prev,
                [groupId]: false,
            }
        })
        setNewTaskInputs((prev) => ({
            ...prev,
            [groupId]: '',
        }))
        setCreateTaskStateFor(groupId, {
            ...initialCreateTask,
            open: true,
            position: position, // 'top' or 'bottom'
        })
    }

    // Expose function to add task at top of first swimlane from toolbar action
    useEffect(() => {
        if (!onAddTaskToFirstSwimlane) return

        onAddTaskToFirstSwimlane.current = (type = 'Task') => {
            if (!canEditModule) return
            const firstSwimlane = (localTaskList || projectTaskList)?.[0]
            const firstSwimlaneId = firstSwimlane?._id
            if (!firstSwimlaneId) return

            // Ensure first swimlane is expanded before showing top add-task row
            setCollapsedSections((prev) => ({
                ...prev,
                [firstSwimlaneId]: false,
            }))
            setNewTaskInputs((prev) => ({
                ...prev,
                [firstSwimlaneId]: '',
            }))
            setCreateTaskStateFor(firstSwimlaneId, {
                open: true,
                loading: false,
                title: '',
                assigneeId: null,
                assigneeData: null,
                start: null,
                end: null,
                position: 'top',
                type: type === 'Bug' ? 'Bug' : 'Task',
            })
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        localTaskList,
        projectTaskList,
        onAddTaskToFirstSwimlane,
        canEditModule,
    ])

    const handleNewTaskInputChange = (groupId, value) => {
        newTaskInputsRef.current = {
            ...newTaskInputsRef.current,
            [groupId]: value,
        }
        setNewTaskInputs((prev) => ({
            ...prev,
            [groupId]: value,
        }))
    }

    const setNewTaskAssigneeOpenFor = (groupId, open) => {
        newTaskAssigneeOpenRef.current = {
            ...newTaskAssigneeOpenRef.current,
            [groupId]: open,
        }
        setNewTaskAssigneeOpen((prev) => ({
            ...prev,
            [groupId]: open,
        }))
    }

    const setNewTaskDateOpenFor = (groupId, open) => {
        newTaskDateOpenRef.current = {
            ...newTaskDateOpenRef.current,
            [groupId]: open,
        }
        setNewTaskDateOpen((prev) => ({
            ...prev,
            [groupId]: open,
        }))
    }

    const resetNewTaskDraft = (groupId) => {
        newTaskInputsRef.current = {
            ...newTaskInputsRef.current,
            [groupId]: '',
        }
        setNewTaskInputs((prev) => ({
            ...prev,
            [groupId]: '',
        }))
        setNewTaskAssigneeOpenFor(groupId, false)
        setNewTaskDateOpenFor(groupId, false)
        setCreateTaskStateFor(groupId, initialCreateTask)
    }

    const finishNewTaskDraft = (groupId) => {
        if (
            newTaskAssigneeOpenRef.current[groupId] ||
            newTaskDateOpenRef.current[groupId]
        ) {
            return
        }

        const active = document.activeElement
        if (
            active?.closest?.(
                `[data-new-task-row="${String(groupId).replace(/"/g, '\\"')}"]`
            )
        ) {
            return
        }

        // Date/assignee popovers render in document.body — stay drafting while focus is there.
        if (
            active?.closest?.(
                '.ant-popover:not(.ant-popover-hidden), .ant-picker-dropdown'
            )
        ) {
            return
        }

        const taskName = newTaskInputsRef.current[groupId]?.trim()
        if (taskName) {
            createTaskFromList(taskName, groupId)
        } else {
            resetNewTaskDraft(groupId)
        }
    }

    const handleNewTaskAssigneeChange = (groupId, selected) => {
        const assigneeData =
            selected && typeof selected === 'object'
                ? {
                      _id: selected?._id,
                      name: selected?.name,
                      email: selected?.email,
                      profilePicture: selected?.profilePicture,
                  }
                : null

        setCreateTaskStateFor(groupId, (current) => ({
            ...current,
            assigneeId: selected?._id || selected || null,
            assigneeData,
        }))
        setNewTaskAssigneeOpenFor(groupId, false)
    }

    const handleNewTaskAssigneeOpenChange = (groupId, open) => {
        setNewTaskAssigneeOpenFor(groupId, open)
        if (!open) {
            // Defer so Tab→date can open the date picker first.
            setTimeout(() => finishNewTaskDraft(groupId), 200)
        }
    }

    const handleNewTaskAssigneeTabNext = (groupId) => {
        setNewTaskAssigneeOpenFor(groupId, false)
        setNewTaskDateOpenFor(groupId, true)
    }

    const handleNewTaskDateChange = (groupId, dateRange) => {
        setCreateTaskStateFor(groupId, (current) => ({
            ...current,
            start: dateRange?.start || null,
            end: dateRange?.end || null,
        }))
    }

    const handleNewTaskDateOpenChange = (groupId, open) => {
        setNewTaskDateOpenFor(groupId, open)
        if (!open) {
            setTimeout(() => finishNewTaskDraft(groupId), 200)
        }
    }

    const handleNewTaskInputKeyDown = (e, groupId) => {
        if (e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault()
            skipNewTaskBlurCreateRef.current = true
            setNewTaskDateOpenFor(groupId, false)
            setNewTaskAssigneeOpenFor(groupId, true)
            return
        }

        if (e.key === 'Enter') {
            e.preventDefault()
            const taskName = newTaskInputsRef.current[groupId]?.trim()
            if (taskName) {
                createTaskFromList(taskName, groupId)
            }
        } else if (e.key === 'Escape') {
            resetNewTaskDraft(groupId)
        }
    }

    const handleNewTaskInputBlur = (groupId) => {
        setTimeout(() => {
            if (skipNewTaskBlurCreateRef.current) {
                skipNewTaskBlurCreateRef.current = false
                return
            }
            finishNewTaskDraft(groupId)
        }, 200)
    }

    const getInitials = (name) => {
        if (!name) return 'U'
        const parts = name.split(' ')
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase()
        }
        return name.substring(0, 2).toUpperCase()
    }

    const getAssigneeColor = (name) => {
        if (!name) return '#e2e8f0'
        const colors = [
            '#85BBF9',
            '#6366f1',
            '#8b5cf6',
            '#ec4899',
            '#f59e0b',
            '#10b981',
            '#06b6d4',
        ]
        const index = name.charCodeAt(0) % colors.length
        return colors[index]
    }

    const executeTaskUpdate = async (taskId, updates) => {
        // Always read the latest list so optimistic assignee/date edits are included
        let task = null
        for (const group of localTaskListRef.current || projectTaskList || []) {
            const foundTask = group?.list?.find((t) => t?._id === taskId)
            if (foundTask) {
                task = foundTask
                break
            }
        }

        if (!task) {
            message.error('Task not found')
            return Promise.reject(new Error('Task not found'))
        }

        const resolvedAssigneeId =
            updates.assigneeId !== undefined
                ? resolveReferenceId(updates.assigneeId)
                : resolveReferenceId(task?.assigneeId)

        try {
            let result
            if (isMain) {
                const body = buildMainTaskUpdateBodyFromTask(task, projectId, {
                    title: updates.title ?? task?.title,
                    assigneeId:
                        updates.assigneeId !== undefined
                            ? resolvedAssigneeId
                            : undefined,
                    start:
                        updates.start !== undefined
                            ? updates.start
                            : task?.start,
                    end: updates.end !== undefined ? updates.end : task?.end,
                })
                result = await dispatch(mainTaskUpdate({ id: taskId, body }))
            } else {
                const payload = {
                    _id: taskId,
                    updateTask: {
                        title: updates.title ?? task?.title,
                        projectId: projectId,
                        type: task?.type || 'Task',
                        assigneeId: resolvedAssigneeId,
                        priority: task?.priority || 'Low',
                        status: task?.status || 'To Do',
                        description: task?.description,
                        start:
                            updates.start !== undefined
                                ? updates.start
                                : task?.start,
                        end:
                            updates.end !== undefined ? updates.end : task?.end,
                    },
                }
                result = await dispatch(taskUpdateSupport(payload))
            }

            const data = result?.payload?.data
            if (isMain ? isTaskMutationSuccess(data) : data?.success) {
                // Only drop fields this request actually sent; keep newer pending edits
                const pending = pendingUpdatesRef.current[taskId]
                if (pending) {
                    const remaining = { ...pending }
                    Object.keys(updates).forEach((key) => {
                        if (Object.is(remaining[key], updates[key])) {
                            delete remaining[key]
                        }
                    })
                    if (Object.keys(remaining).length === 0) {
                        delete pendingUpdatesRef.current[taskId]
                    } else {
                        pendingUpdatesRef.current[taskId] = remaining
                    }
                }

                refreshTasks(0)
                return Promise.resolve()
            } else {
                const errorMessage = data?.message || 'Failed to update task'
                message.error(errorMessage)
                return Promise.reject(new Error(errorMessage))
            }
        } catch (error) {
            message.error('Failed to update task')
            return Promise.reject(error)
        }
    }

    const debouncedTaskUpdate = (taskId, updates) => {
        // Clear existing timer for this task
        if (updateTimers.current[taskId]) {
            clearTimeout(updateTimers.current[taskId])
        }

        // Merge with existing pending updates
        pendingUpdatesRef.current[taskId] = {
            ...pendingUpdatesRef.current[taskId],
            ...updates,
        }

        // Set new timer
        updateTimers.current[taskId] = setTimeout(() => {
            const merged = pendingUpdatesRef.current[taskId]
            if (!merged) return
            // Take ownership so an in-flight success can't wipe newer edits
            delete pendingUpdatesRef.current[taskId]
            delete updateTimers.current[taskId]
            executeTaskUpdate(taskId, merged).catch(() => {
                pendingUpdatesRef.current[taskId] = {
                    ...merged,
                    ...pendingUpdatesRef.current[taskId],
                }
            })
        }, 500) // 500ms debounce delay
    }

    const handleAssigneeChange = (task, selected) => {
        if (!canEditModule) return
        const newAssigneeId = selected?._id || selected
        const currentAssigneeId =
            task?.assigneeId?.[0]?._id ??
            task?.assigneeId?._id ??
            task?.assigneeId
        if (newAssigneeId === currentAssigneeId) {
            return
        }

        setLocalTaskList((prev) =>
            (prev || []).map((group) =>
                Array.isArray(group?.list)
                    ? {
                          ...group,
                          list: group.list.map((item) =>
                              item?._id === task?._id
                                  ? {
                                        ...item,
                                        assigneeId: {
                                            name: selected?.name,
                                            _id: selected?._id,
                                            email: selected?.email,
                                            profilePicture:
                                                selected?.profilePicture,
                                        },
                                    }
                                  : item
                          ),
                      }
                    : group
            )
        )

        debouncedTaskUpdate(task?._id, { assigneeId: newAssigneeId })
    }

    const getTaskCompleted = (task) =>
        Boolean(
            task?.isCompleted ??
            task?.isComplete ??
            ['completed', 'done'].includes(
                String(task?.status || '')
                    .trim()
                    .toLowerCase()
            )
        )

    const handleToggleTaskComplete = async (task) => {
        if (!canEditModule) return
        const taskId = task?._id
        if (!taskId || completingTasks[taskId]?.isToggling) return

        const nextCompleted = !getTaskCompleted(task)

        if (nextCompleted) {
            triggerCompletionCelebration(taskId)
        } else {
            clearCompletionCelebration()
        }

        setCompletingTasks((prev) => ({
            ...prev,
            [taskId]: { ...prev[taskId], isToggling: true },
        }))

        try {
            const payload = {
                _id: taskId,
                updateTask: {
                    title: task?.title || '',
                    projectId: projectId,
                    type: task?.type || 'Task',
                    assigneeId:
                        task?.assigneeId?.[0]?._id ??
                        task?.assigneeId?._id ??
                        task?.assigneeId ??
                        null,
                    priority: task?.priority || 'Low',
                    status: task?.status || 'To Do',
                    description: task?.description,
                    start: task?.start || null,
                    end: task?.end || null,
                    isCompleted: nextCompleted,
                },
            }
            const result = await dispatch(taskUpdateSupport(payload))
            const data = result?.payload?.data
            if (data?.success) {
                refreshTasks(0)
            } else {
                clearCompletionCelebration()
                showActionNotification({
                    type: 'error',
                    message:
                        data?.message || 'Failed to update task completion',
                })
            }
        } catch {
            clearCompletionCelebration()
            showActionNotification({
                type: 'error',
                message: 'Failed to update task completion',
            })
        } finally {
            setCompletingTasks((prev) => ({
                ...prev,
                [taskId]: { ...prev[taskId], isToggling: false },
            }))
        }
    }

    const handleOpenTaskFromList = React.useCallback(
        (taskId, focus) => {
            clearCompletionCelebration()
            onTaskClick?.(taskId, focus)
        },
        [clearCompletionCelebration, onTaskClick]
    )

    const handleDateRangeChange = (task, dateRange) => {
        if (!canEditModule) return
        setLocalTaskList((prev) =>
            (prev || []).map((group) =>
                Array.isArray(group?.list)
                    ? {
                          ...group,
                          list: group.list.map((item) =>
                              item?._id === task?._id
                                  ? {
                                        ...item,
                                        start: dateRange?.start || null,
                                        end: dateRange?.end || null,
                                    }
                                  : item
                          ),
                      }
                    : group
            )
        )

        debouncedTaskUpdate(task?._id, {
            start: dateRange?.start || null,
            end: dateRange?.end || null,
        })
    }

    const handleEpicChange = async (task, epicId) => {
        if (!isMain) return
        const currentEpicId = getTaskEpicId(task)
        if (String(epicId ?? '') === String(currentEpicId ?? '')) return

        setLocalTaskList((prev) =>
            (prev || []).map((group) =>
                Array.isArray(group?.list)
                    ? {
                          ...group,
                          list: group.list.map((item) =>
                              item?._id === task?._id
                                  ? { ...item, epic: epicId, epicId }
                                  : item
                          ),
                      }
                    : group
            )
        )

        try {
            const body = buildMainTaskUpdateBodyFromTask(task, projectId, {
                epic: epicId,
            })
            const result = await dispatch(
                mainTaskUpdate({ id: task._id, body })
            )
            const data = result?.payload?.data
            if (!isTaskMutationSuccess(data)) {
                showActionNotification({
                    type: 'error',
                    message:
                        result?.payload?.data?.message ||
                        'Failed to update epic',
                })
            }
        } catch {
            showActionNotification({
                type: 'error',
                message: 'Failed to update epic',
            })
        }
    }

    const handleSprintChange = async (task, sprintId) => {
        if (!isMain) return
        const currentSprintId = getTaskSprintId(task)
        if (String(sprintId ?? '') === String(currentSprintId ?? '')) return

        setLocalTaskList((prev) =>
            (prev || []).map((group) =>
                Array.isArray(group?.list)
                    ? {
                          ...group,
                          list: group.list.map((item) =>
                              item?._id === task?._id
                                  ? { ...item, sprintId, sprint: sprintId }
                                  : item
                          ),
                      }
                    : group
            )
        )

        try {
            const body = buildMainTaskUpdateBodyFromTask(task, projectId, {
                sprintId,
            })
            const result = await dispatch(
                mainTaskUpdate({ id: task._id, body })
            )
            const data = result?.payload?.data
            if (!isTaskMutationSuccess(data)) {
                showActionNotification({
                    type: 'error',
                    message:
                        result?.payload?.data?.message ||
                        'Failed to update sprint',
                })
            }
        } catch {
            showActionNotification({
                type: 'error',
                message: 'Failed to update sprint',
            })
        }
    }

    const renderDetailsChevron = () => (
        <Tooltip title="Details" placement="top">
            <Icon
                icon="mdi:chevron-right"
                width={16}
                height={16}
                style={{ color: '#94a3b8' }}
            />
        </Tooltip>
    )

    const getTaskSubtaskCount = (task) => {
        const visibleSubTasks = filterSupportQuickFilterSubTasks(
            task?.subTasks,
            listQuery,
            { applyFilter: !isMain }
        )
        if (Array.isArray(task?.subTasks)) return visibleSubTasks.length
        // No nested list available — fall back to API count only when
        // quick filters are not narrowing the visible set.
        if (!isMain && listQuery) {
            const hasQuickFilter =
                typeof listQuery.isCompleted === 'boolean' ||
                (Array.isArray(listQuery.mineAssignee) &&
                    listQuery.mineAssignee.length > 0)
            if (hasQuickFilter) return visibleSubTasks.length
        }
        return Number(task?.subTasksLength || task?.subTaskCount || 0)
    }

    const renderTaskSubtaskMeta = (task) => {
        const subtaskCount = getTaskSubtaskCount(task)
        if (subtaskCount <= 0) return null
        return (
            <Tooltip title="View subtasks" placement="top">
                <div
                    className={styles.taskMeta}
                    onClick={(e) => {
                        e.stopPropagation()
                        handleOpenTaskFromList(task?._id, 'subtasks')
                    }}
                    style={{ cursor: 'pointer' }}
                >
                    <span className={styles.commentCount}>{subtaskCount}</span>
                    <Icon
                        icon="mdi:source-branch"
                        width={14}
                        height={14}
                        className={styles.commentIcon}
                    />
                </div>
            </Tooltip>
        )
    }

    const renderTaskEpicSprintGroup = (task) => (
        <div
            className={styles.taskEpicSprintGroup}
            data-ignore-click
            onClick={(e) => e.stopPropagation()}
        >
            <EpicSelector
                projectId={projectId}
                value={getTaskEpicId(task)}
                displayOption={getEpicDisplayOptionFromTask(task)}
                onChange={(epicId) => handleEpicChange(task, epicId)}
                compact
                placeholder="Epic"
            />
            <SprintSelector
                projectId={projectId}
                value={getTaskSprintId(task)}
                displayOption={getSprintDisplayOptionFromTask(task)}
                onChange={(sprintId) => handleSprintChange(task, sprintId)}
                compact
                placeholder="Sprint"
            />
        </div>
    )

    const renderTaskTrailing = (task) =>
        isMain ? (
            <div className={styles.taskNameTrailing}>
                {renderTaskEpicSprintGroup(task)}
                <div className={styles.taskActions}>
                    {renderDetailsChevron()}
                </div>
            </div>
        ) : (
            <div className={styles.taskActions}>{renderDetailsChevron()}</div>
        )

    const getCaretPosition = (element, clickX) => {
        const text = element.textContent || element.innerText || ''
        if (!text) return 0

        const style = window.getComputedStyle(element)
        const canvas = document.createElement('canvas')
        const context = canvas.getContext('2d')

        // Get font properties
        const fontSize = style.fontSize || '14px'
        const fontFamily = style.fontFamily || 'Arial'
        context.font = `${fontSize} ${fontFamily}`

        const rect = element.getBoundingClientRect()
        const relativeX = clickX - rect.left

        // Account for padding
        const paddingLeft = parseInt(style.paddingLeft) || 0
        const adjustedX = relativeX - paddingLeft

        // Binary search for better performance with long text
        let low = 0
        let high = text.length
        let position = text.length

        while (low <= high) {
            const mid = Math.floor((low + high) / 2)
            const textBefore = text.substring(0, mid)
            const width = context.measureText(textBefore).width

            if (width < adjustedX) {
                low = mid + 1
                position = mid + 1
            } else {
                high = mid - 1
                position = mid
            }
        }

        // Fine-tune: check if we should be at the previous character
        if (position > 0) {
            const textBefore = text.substring(0, position - 1)
            const widthBefore = context.measureText(textBefore).width
            const charWidth = context.measureText(text[position - 1]).width

            if (adjustedX - widthBefore < charWidth / 2) {
                position = position - 1
            }
        }

        return Math.max(0, Math.min(position, text.length))
    }

    const handleTaskTitleEdit = (taskId, currentTitle, e) => {
        e.stopPropagation()
        if (!canEditModule) return

        // Calculate cursor position based on click
        let cursorPosition = currentTitle.length
        if (e.target && e.target.tagName === 'SPAN') {
            try {
                cursorPosition = getCaretPosition(e.target, e.clientX)
            } catch (error) {
                // Fallback to end if calculation fails
                cursorPosition = currentTitle.length
            }
        }

        setEditingTasks((prev) => ({
            ...prev,
            [taskId]: {
                isEditing: true,
                editedTitle: currentTitle,
                isUpdating: false,
            },
        }))

        // Store cursor position in a variable that will be accessible in setTimeout
        const finalCursorPosition = cursorPosition

        setTimeout(() => {
            const inputElement = taskInputRefs.current[taskId]
            if (inputElement) {
                // Get the actual input element (Ant Design Input wraps it)
                const input = inputElement.input || inputElement
                if (input) {
                    input.focus()
                    // Set cursor position to where user clicked
                    if (input.setSelectionRange) {
                        input.setSelectionRange(
                            finalCursorPosition,
                            finalCursorPosition
                        )
                    }
                }
            }
        }, 0)
    }

    /**
     * Leaving edit mode drops back to the title held in the list, so without
     * this the row re-renders the pre-edit text until refreshTasks() has
     * round-tripped — the old title visibly reappears for a second or two.
     * Patch locally first, exactly as the assignee/date handlers do.
     */
    const patchLocalTaskTitle = (taskId, title) => {
        setLocalTaskList((prev) =>
            (prev || []).map((group) =>
                Array.isArray(group?.list)
                    ? {
                          ...group,
                          list: group.list.map((item) =>
                              item?._id === taskId ? { ...item, title } : item
                          ),
                      }
                    : group
            )
        )
    }

    const handleTaskTitleSave = (task) => {
        const editingState = editingTasks[task?._id]
        if (!editingState) return

        const editedTitle = editingState.editedTitle

        if (editedTitle.trim() === task?.title.trim()) {
            setEditingTasks((prev) => {
                const newState = { ...prev }
                delete newState[task?._id]
                return newState
            })
            return
        }

        if (!editedTitle.trim()) {
            // Clearing the field and clicking away is a cancel, not a failure:
            // the row keeps its existing title and nothing is sent, so an error
            // here just contradicts what the user sees.
            setEditingTasks((prev) => {
                const newState = { ...prev }
                delete newState[task?._id]
                return newState
            })
            return
        }

        // Clear any pending timer and execute immediately for title changes
        if (updateTimers.current[task?._id]) {
            clearTimeout(updateTimers.current[task?._id])
            delete updateTimers.current[task?._id]
        }

        // Get pending updates and merge with title
        const pending = pendingUpdatesRef.current[task?._id] || {}
        const allUpdates = {
            ...pending,
            title: editedTitle.trim(),
        }

        // Clear any pending timer and updates since we're executing immediately
        if (updateTimers.current[task?._id]) {
            clearTimeout(updateTimers.current[task?._id])
            delete updateTimers.current[task?._id]
        }
        delete pendingUpdatesRef.current[task?._id]

        // Execute update immediately for title
        const previousTitle = task?.title
        patchLocalTaskTitle(task?._id, allUpdates.title)

        setEditingTasks((prev) => ({
            ...prev,
            [task?._id]: {
                ...prev[task?._id],
                isUpdating: true,
            },
        }))

        executeTaskUpdate(task?._id, allUpdates)
            .then(() => {
                setEditingTasks((prev) => {
                    const newState = { ...prev }
                    delete newState[task?._id]
                    return newState
                })
            })
            .catch(() => {
                patchLocalTaskTitle(task?._id, previousTitle)
                setEditingTasks((prev) => ({
                    ...prev,
                    [task?._id]: {
                        ...prev[task?._id],
                        editedTitle: previousTitle,
                        isUpdating: false,
                    },
                }))
            })
    }

    const handleTaskTitleCancel = (taskId) => {
        setEditingTasks((prev) => {
            const newState = { ...prev }
            delete newState[taskId]
            return newState
        })
    }

    const handleTaskTitleKeyDown = (e, task) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleTaskTitleSave(task)
        } else if (e.key === 'Escape') {
            e.preventDefault()
            handleTaskTitleCancel(task?._id)
        }
    }

    const handleSectionMenuOpenChange = (sectionId, open) => {
        setSectionMenuOpen((prev) => ({
            ...prev,
            [sectionId]: open,
        }))
    }

    const handleRenameSection = (sectionId) => {
        // Find the section to get its current name
        const section = (localTaskList || projectTaskList).find(
            (group) => group?._id === sectionId
        )
        if (section) {
            setEditingSectionId(sectionId)
            setEditingSectionName(sectionId || 'Unassigned')
            setSectionMenuOpen((prev) => ({
                ...prev,
                [sectionId]: false,
            }))
            // Focus the input after state update
            setTimeout(() => {
                sectionNameInputRef.current?.focus()
                sectionNameInputRef.current?.select()
            }, 0)
        }
    }

    const handleSectionNameBlur = async (sectionId) => {
        if (!editingSectionId || editingSectionId !== sectionId) return

        const trimmedName = editingSectionName.trim()
        if (!trimmedName) {
            // Revert to original name if empty
            setEditingSectionName(sectionId || 'Unassigned')
            setEditingSectionId(null)
            return
        }

        // Only update if name changed
        if (trimmedName !== sectionId) {
            try {
                const result = await dispatch(
                    createSwimlaneAction({
                        id: projectId,
                        fieldName: 'update',
                        oldName: sectionId,
                        newName: trimmedName,
                        ...swimlaneTypePayload,
                    })
                )

                if (result?.payload?.data?.success) {
                    showActionNotification({
                        message: 'Section renamed successfully',
                        subMessage: `${sectionId} → ${trimmedName}`,
                        actionButtonText: 'Undo',
                        notificationKey: 'section-renamed',
                    })

                    // Reset editing state
                    setEditingSectionId(null)
                    setEditingSectionName('')

                    // Update local state
                    setLocalTaskList((prev) =>
                        prev.map((group) =>
                            group?._id === sectionId
                                ? { ...group, _id: trimmedName }
                                : group
                        )
                    )

                    refreshTasks(0)
                } else {
                    message.error(
                        result?.payload?.data?.message ||
                            'Failed to rename section'
                    )
                    // Revert to original name on error
                    setEditingSectionName(sectionId || 'Unassigned')
                    setEditingSectionId(null)
                }
            } catch (error) {
                message.error('Failed to rename section')
                // Revert to original name on error
                setEditingSectionName(sectionId || 'Unassigned')
            }
        }

        setEditingSectionId(null)
    }

    const handleSectionNameKeyDown = (sectionId, e) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            handleSectionNameBlur(sectionId)
        } else if (e.key === 'Escape') {
            // Revert to original name
            setEditingSectionName(sectionId || 'Unassigned')
            setEditingSectionId(null)
        }
    }

    const handleDeleteSection = (sectionId) => {
        if (!canDeleteModule) return
        // Find the section/group data
        const section = (localTaskList || projectTaskList).find(
            (group) => group?._id === sectionId
        )

        if (!section) {
            message.error('Section not found')
            setSectionMenuOpen((prev) => ({
                ...prev,
                [sectionId]: false,
            }))
            return
        }

        // Calculate task counts
        const tasks = section?.list || []

        setDeleteSectionData({
            sectionId,
            sectionName: sectionId || 'Unassigned',
            totalTasks: tasks.length,
            position: section?.position ?? 0,
        })
        setDeleteOption(tasks.length > 0 ? 'keep' : 'delete')
        setDeleteModalOpen(true)
        setSectionMenuOpen((prev) => ({
            ...prev,
            [sectionId]: false,
        }))
    }

    const handleConfirmDeleteSection = async () => {
        if (!deleteSectionData || deleteSectionLoading) return

        setDeleteSectionLoading(true)
        try {
            const result = await dispatch(
                createSwimlaneAction({
                    id: projectId,
                    fieldName: 'remove',
                    name: deleteSectionData.sectionName,
                    position: deleteSectionData.position,
                    deleteTasks: deleteOption === 'delete',
                    ...swimlaneTypePayload,
                })
            )

            if (result?.payload?.data?.success) {
                const sectionName = deleteSectionData.sectionName || 'Section'
                const hadTasks = (deleteSectionData.totalTasks || 0) > 0
                const destinationSection =
                    (localTaskList || projectTaskList)?.find(
                        (group) =>
                            group?._id &&
                            group._id !== deleteSectionData.sectionId
                    )?._id || 'To Do'

                let subMessage = ''
                if (hadTasks && deleteOption === 'keep') {
                    subMessage = `All tasks/bugs have been moved to the "${destinationSection}" panel.`
                } else if (hadTasks && deleteOption === 'delete') {
                    subMessage =
                        'All tasks/bugs in this section have been deleted.'
                }

                showActionNotification({
                    type: 'success',
                    message: `${sectionName} Section deleted successfully.`,
                    subMessage,
                    actionButtonText: 'Undo',
                    notificationKey: 'section-deleted',
                })
                setDeleteModalOpen(false)
                setDeleteSectionData(null)
                setDeleteOption('keep')

                refreshTasks(0)
            } else {
                showActionNotification({
                    message: 'Failed to delete section',
                    subMessage: result?.payload?.data?.message,
                    actionButtonText: 'Undo',
                    notificationKey: 'section-deleted',
                })
            }
        } catch (error) {
            message.error('Failed to delete section')
        } finally {
            setDeleteSectionLoading(false)
        }
    }

    const handleModalCancel = () => {
        if (deleteSectionLoading) return
        setDeleteModalOpen(false)
        setDeleteSectionData(null)
        setDeleteOption('keep')
    }

    const createSwimlane = async (sectionName) => {
        if (!sectionName || !sectionName.trim()) {
            //setIsAddingSection(false)
            return
        }

        if (!projectId) {
            message.error('Project ID is required')
            //setIsAddingSection(false)
            return
        }

        // Calculate position based on existing swimlanes
        let newPosition = 0
        if (
            Array.isArray(localTaskList || projectTaskList) &&
            (localTaskList || projectTaskList).length > 0
        ) {
            const maxPosition = Math.max(
                ...(localTaskList || projectTaskList).map(
                    (group) => group?.position ?? 0
                )
            )
            newPosition = maxPosition + 1
        }

        // setIsCreatingSection(true)

        try {
            const result = await dispatch(
                createSwimlaneAction({
                    id: projectId,
                    name: sectionName.trim(),
                    position: newPosition.toString(),
                    ...swimlaneTypePayload,
                })
            )

            if (result?.payload?.data?.success) {
                showActionNotification({
                    message: 'Section created successfully',
                    subMessage: sectionName.trim(),
                    actionButtonText: 'Undo',
                    notificationKey: 'section-created',
                })
                // setNewSectionName('')
                //setIsAddingSection(false)

                refreshTasks(0)
            } else {
                message.error(
                    result?.payload?.data?.message || 'Failed to create section'
                )
            }
        } catch (error) {
            message.error('Failed to create section')
        } finally {
            // setIsCreatingSection(false)
        }
    }

    /*  const handleAddSection = () => {
        setIsAddingSection(true)
        setNewSectionName('')
        setTimeout(() => {
            addSectionInputRef.current?.focus()
        }, 0)
    } */

    /* const handleSectionInputBlur = () => {
        // Use setTimeout to allow click events to process first
        setTimeout(() => {
            if (newSectionName.trim()) {
                createSwimlane(newSectionName)
            } else {
                setIsAddingSection(false)
                setNewSectionName('')
            }
        }, 200)
    } */

    /*  const handleSectionInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            if (newSectionName.trim()) {
                createSwimlane(newSectionName)
            }
        } else if (e.key === 'Escape') {
            e.preventDefault()
            setIsAddingSection(false)
            setNewSectionName('')
        }
    } */

    const renderSectionMenu = (sectionId) => {
        // Calculate total number of swimlanes/sections
        const totalSections =
            (localTaskList || projectTaskList)?.filter(
                (group) => group?.list !== undefined
            )?.length || 0
        const isOnlyOneSection = totalSections <= 1

        return (
            <div className={styles.sectionMenu}>
                <div
                    className={styles.menuItem}
                    onClick={() => handleRenameSection(sectionId)}
                >
                    <span className={styles.menuItemIcon}>
                        <Icon
                            icon="mdi:pencil-outline"
                            width={16}
                            height={16}
                        />
                    </span>
                    <span className={styles.menuItemLabel}>Rename section</span>
                </div>

                {canDeleteModule && (
                    <>
                        <div className={styles.menuDivider} />
                        <div
                            className={`${styles.menuItem} ${styles.deleteMenuItem} ${
                                isOnlyOneSection ? styles.disabledMenuItem : ''
                            }`}
                            onClick={
                                isOnlyOneSection
                                    ? undefined
                                    : () => handleDeleteSection(sectionId)
                            }
                        >
                            <span className={styles.menuItemIcon}>
                                <Icon
                                    icon="mdi:trash-can-outline"
                                    width={16}
                                    height={16}
                                />
                            </span>
                            <span className={styles.menuItemLabel}>
                                Delete section
                            </span>
                        </div>
                    </>
                )}
            </div>
        )
    }

    // Transform data for Table with expandable rows.
    // Memoised: antd Table diffs dataSource by identity, so rebuilding these
    // rows on every render made it re-render the whole table on any state change.
    const tableSource = localTaskList || projectTaskList
    const tableData = React.useMemo(
        () =>
            (tableSource || []).map((group) => {
                const tasks = (group?.list || []).filter(
                    (task) => !task?.parentTask?.length
                )

                return {
                    key: `section-${group?._id}`,
                    sectionId: group?._id,
                    sectionName: group?._id || 'Unassigned',
                    isSection: true,
                    taskCount: tasks.length,
                    // Don't set children - we'll render them manually in expandedRowRender
                    children: [],
                    // Store tasks data for manual rendering
                    tasksData: tasks.map((task) => ({
                        key: `task-${task?._id}`,
                        taskId: task?._id,
                        task: task,
                        isSection: false,
                        sectionId: group?._id,
                    })),
                }
            }),
        [tableSource]
    )

    // Table columns
    const columns = [
        {
            title: 'Name',
            key: 'name',
            dataIndex: 'name',
            width: '40%',
            render: (text, record) => {
                if (record.isSection) {
                    // Section header row
                    return (
                        <div className={styles.sectionHeaderRow}>
                            <div className={styles.sectionHeaderLeft}>
                                {editingSectionId === record.sectionId ? (
                                    <Input
                                        ref={sectionNameInputRef}
                                        value={editingSectionName}
                                        onChange={(e) =>
                                            setEditingSectionName(
                                                e.target.value
                                            )
                                        }
                                        onBlur={() =>
                                            handleSectionNameBlur(
                                                record.sectionId
                                            )
                                        }
                                        onKeyDown={(e) =>
                                            handleSectionNameKeyDown(
                                                record.sectionId,
                                                e
                                            )
                                        }
                                        className={styles.sectionNameInput}
                                        autoFocus
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                ) : (
                                    <span className={styles.sectionTitle}>
                                        {record.sectionName}
                                    </span>
                                )}
                            </div>
                            {canEditModule && (
                                <div className={styles.sectionHeaderRight}>
                                    <Tooltip title="Add Task">
                                        <Icon
                                            icon="mdi:plus"
                                            width={20}
                                            height={20}
                                            className={
                                                styles.columnHeaderPlusIcon
                                            }
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                handleAddTaskClick(
                                                    record.sectionId,
                                                    'top'
                                                )
                                            }}
                                            style={{
                                                cursor: 'pointer',
                                                color: '#636363',
                                            }}
                                        />
                                    </Tooltip>
                                    <Popover
                                        content={renderSectionMenu(
                                            record.sectionId
                                        )}
                                        trigger="click"
                                        open={sectionMenuOpen[record.sectionId]}
                                        onOpenChange={(open) =>
                                            handleSectionMenuOpenChange(
                                                record.sectionId,
                                                open
                                            )
                                        }
                                        placement="bottomRight"
                                        overlayClassName={
                                            styles.sectionMenuPopover
                                        }
                                    >
                                        <Tooltip title="More options">
                                            <Icon
                                                icon="weui:more-outlined"
                                                width={20}
                                                height={20}
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                }}
                                                style={{
                                                    cursor: 'pointer',
                                                    color: '#636363',
                                                }}
                                            />
                                        </Tooltip>
                                    </Popover>
                                </div>
                            )}
                        </div>
                    )
                } else {
                    // Task row
                    const task = record.task

                    return (
                        <div
                            className={styles.taskNameCell}
                            onClick={() => handleOpenTaskFromList(task?._id)}
                        >
                            <span
                                data-ignore-click
                                onClick={(e) => {
                                    e.stopPropagation()
                                    if (!canEditModule) return
                                    handleToggleTaskComplete(task)
                                }}
                                onMouseEnter={() => {
                                    if (!canEditModule) return
                                    setCompletingTasks((prev) => ({
                                        ...prev,
                                        [task?._id]: {
                                            ...prev[task?._id],
                                            isHovered: true,
                                        },
                                    }))
                                }}
                                onMouseLeave={() => {
                                    if (!canEditModule) return
                                    setCompletingTasks((prev) => ({
                                        ...prev,
                                        [task?._id]: {
                                            ...prev[task?._id],
                                            isHovered: false,
                                        },
                                    }))
                                }}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 18,
                                    height: 18,
                                    borderRadius: '50%',
                                    border: `1px solid ${
                                        getTaskCompleted(task)
                                            ? 'transparent'
                                            : completingTasks[task?._id]
                                                    ?.isHovered
                                              ? '#5da182'
                                              : '#cbd5e1'
                                    }`,
                                    backgroundColor: getTaskCompleted(task)
                                        ? '#5da182'
                                        : completingTasks[task?._id]?.isHovered
                                          ? '#f0fdf4'
                                          : 'transparent',
                                    cursor:
                                        !canEditModule ||
                                        completingTasks[task?._id]?.isToggling
                                            ? 'default'
                                            : 'pointer',
                                    opacity: completingTasks[task?._id]
                                        ?.isToggling
                                        ? 0.6
                                        : 1,
                                    transition:
                                        'all 150ms ease, border-color 150ms ease, background-color 150ms ease',
                                    flexShrink: 0,
                                }}
                            >
                                <Icon
                                    icon="iconamoon:check-light"
                                    width={13}
                                    height={13}
                                    style={{
                                        color: getTaskCompleted(task)
                                            ? '#ffffff'
                                            : completingTasks[task?._id]
                                                    ?.isHovered
                                              ? '#5da182'
                                              : '#94a3b8',
                                        transition: 'color 150ms ease',
                                    }}
                                />
                            </span>

                            {editingTasks[task?._id]?.isEditing ? (
                                <Input
                                    ref={(el) => {
                                        if (el) {
                                            taskInputRefs.current[task?._id] =
                                                el
                                        }
                                    }}
                                    value={
                                        editingTasks[task?._id]?.editedTitle ||
                                        task?.title
                                    }
                                    onChange={(e) =>
                                        setEditingTasks((prev) => ({
                                            ...prev,
                                            [task?._id]: {
                                                ...prev[task?._id],
                                                editedTitle: e.target.value,
                                            },
                                        }))
                                    }
                                    onBlur={() => handleTaskTitleSave(task)}
                                    onKeyDown={(e) =>
                                        handleTaskTitleKeyDown(e, task)
                                    }
                                    className={styles.taskTitleInput}
                                    disabled={
                                        editingTasks[task?._id]?.isUpdating
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    bordered={false}
                                />
                            ) : (
                                <span
                                    className={styles.taskTitle}
                                    onClick={(e) =>
                                        handleTaskTitleEdit(
                                            task?._id,
                                            task?.title,
                                            e
                                        )
                                    }
                                    style={{ cursor: 'text' }}
                                >
                                    {task?.title}
                                </span>
                            )}
                            {renderTaskSubtaskMeta(task)}
                            {task?.commentsLength > 0 && (
                                <Tooltip title="View comments" placement="top">
                                    <div
                                        className={styles.taskMeta}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            handleOpenTaskFromList(
                                                task?._id,
                                                'comments'
                                            )
                                        }}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <span className={styles.commentCount}>
                                            {task?.commentsLength}
                                        </span>
                                        <Icon
                                            icon="iconamoon:comment-light"
                                            width={14}
                                            height={14}
                                            className={styles.commentIcon}
                                        />
                                    </div>
                                </Tooltip>
                            )}
                            {renderTaskTrailing(task)}
                        </div>
                    )
                }
            },
        },
        {
            title: 'Assignee',
            key: 'assignee',
            dataIndex: 'assignee',
            width: '30%',
            render: (text, record) => {
                if (record.isSection) {
                    return null
                }
                const task = record.task
                const selectedAssigneeForDisplay = Array.isArray(
                    task?.assigneeId
                )
                    ? (task?.assigneeId?.[0] ?? null)
                    : (task?.assigneeId ?? null)
                return (
                    <div
                        data-ignore-click
                        className={styles.taskAssigneeCell}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <AssigneeSelectorButton
                            selectedAssignee={selectedAssigneeForDisplay}
                            onSelect={
                                canEditModule
                                    ? (selected) =>
                                          handleAssigneeChange(task, selected)
                                    : undefined
                            }
                            projectId={projectId}
                            filteredAssignees={filteredAssignees}
                            getAssigneeColor={getAssigneeColor}
                            getInitials={getInitials}
                            showName={true}
                            taskListKind={taskListKind}
                        />
                    </div>
                )
            },
        },
        {
            title: 'Due date',
            key: 'dueDate',
            dataIndex: 'dueDate',
            width: '30%',
            render: (text, record) => {
                if (record.isSection) {
                    return null
                }
                const task = record.task
                return (
                    <div
                        data-ignore-click
                        className={styles.taskDateCell}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <DateRangePicker
                            value={{
                                start: task?.start || null,
                                end: task?.end || null,
                            }}
                            onChange={
                                canEditModule
                                    ? (dateRange) =>
                                          handleDateRangeChange(task, dateRange)
                                    : undefined
                            }
                            placeholder=""
                            parentComponent="taskListView"
                        />
                    </div>
                )
            },
        },
    ]

    const renderContent = () => {
        // Skeleton while opening the tab, filtering or searching. Background
        // syncs after a task edit pass `silent`, so they leave the list alone.
        if (
            showRefreshOverlay ||
            (projectTaskListStatus === 'loading' &&
                projectTaskList?.length === 0)
        ) {
            return (
                <div className={styles.listViewOuter}>
                    <ListViewSkeleton />
                </div>
            )
        }

        if (
            projectTaskList?.length === 0 &&
            projectTaskListStatus === 'loaded'
        ) {
            return (
                <div className={styles.listViewOuter}>
                    <div className={styles.emptyState}>
                        <Icon
                            icon="mdi:clipboard-outline"
                            style={{ fontSize: 64, color: '#cbd5e1' }}
                        />
                        <Text className={styles.emptyStateText}>
                            No tasks yet. Create your first task to get started!
                        </Text>
                    </div>
                </div>
            )
        }

        return (
            <div className={styles.listViewOuter}>
                <div
                    className={styles.listViewContainer}
                    ref={listViewContainerRef}
                >
                    <Table
                        columns={columns}
                        dataSource={tableData}
                        pagination={false}
                        showHeader={true}
                        sticky
                        scroll={{ y: tableScrollY }}
                        expandable={{
                            expandedRowKeys: (localTaskList || projectTaskList)
                                .map((group) => group?._id)
                                .filter(
                                    (sectionId) => !collapsedSections[sectionId]
                                )
                                .map((id) => `section-${id}`),
                            onExpand: (expanded, record) => {
                                if (record.isSection) {
                                    handleToggleSection(record.sectionId)
                                }
                            },
                            expandIcon: ({ expanded, onExpand, record }) => {
                                if (!record.isSection) return null
                                return (
                                    <Tooltip
                                        title={
                                            expanded
                                                ? 'Collapse this group'
                                                : 'Expand this group'
                                        }
                                        placement="top"
                                    >
                                        <Icon
                                            icon={
                                                expanded
                                                    ? 'tabler:caret-down-filled'
                                                    : 'tabler:caret-right-filled'
                                            }
                                            width={16}
                                            height={16}
                                            onClick={(e) => onExpand(record, e)}
                                            style={{ cursor: 'pointer' }}
                                        />
                                    </Tooltip>
                                )
                            },
                            expandedRowRender: (record) => {
                                if (!record.isSection) return null

                                const allTasks = record.tasksData || []
                                const visibleTaskCount =
                                    visibleTaskCounts[record.sectionId] ??
                                    INITIAL_VISIBLE_TASKS
                                const isWindowed =
                                    allTasks.length > visibleTaskCount
                                const windowedTasks = isWindowed
                                    ? allTasks.slice(0, visibleTaskCount)
                                    : allTasks
                                // A task added at the bottom lands past the end
                                // of the window. Surface it anyway, so the row
                                // the user just typed stays on screen without
                                // expanding the whole section.
                                const pendingBeyondWindow = isWindowed
                                    ? allTasks
                                          .slice(visibleTaskCount)
                                          .filter((r) => r.task?.pendingCreate)
                                    : []
                                const tasks = pendingBeyondWindow.length
                                    ? [...windowedTasks, ...pendingBeyondWindow]
                                    : windowedTasks
                                const hiddenTaskCount =
                                    allTasks.length - tasks.length
                                const createTaskState =
                                    createTaskStates[record.sectionId]
                                const isCreatingTask = createTaskState?.open
                                const taskPosition =
                                    createTaskState?.position || 'bottom'

                                // Helper function to render new task row
                                const renderNewTaskRow = () => (
                                    <div
                                        className={`${styles.taskRowWrapper} ${styles.newTaskRow}`}
                                        data-new-task-row={record.sectionId}
                                    >
                                        <div
                                            className={styles.taskNameCell}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {taskListKind === 'support' && (
                                                <Icon
                                                    icon="simple-line-icons:check"
                                                    width={16}
                                                    height={16}
                                                    style={{
                                                        color: '#94a3b8',
                                                        minWidth: '16px',
                                                    }}
                                                />
                                            )}
                                            <Input
                                                placeholder="Write a task name"
                                                value={
                                                    newTaskInputs[
                                                        record.sectionId
                                                    ] || ''
                                                }
                                                onChange={(e) =>
                                                    handleNewTaskInputChange(
                                                        record.sectionId,
                                                        e.target.value
                                                    )
                                                }
                                                onKeyDown={(e) =>
                                                    handleNewTaskInputKeyDown(
                                                        e,
                                                        record.sectionId
                                                    )
                                                }
                                                onBlur={() =>
                                                    handleNewTaskInputBlur(
                                                        record.sectionId
                                                    )
                                                }
                                                className={styles.newTaskInput}
                                                autoFocus
                                                disabled={
                                                    createTaskStates[
                                                        record.sectionId
                                                    ]?.loading
                                                }
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                            />
                                            <div className={styles.taskActions}>
                                                {renderDetailsChevron()}
                                            </div>
                                        </div>
                                        <div
                                            className={styles.taskAssigneeCell}
                                            data-ignore-click
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <AssigneeSelectorButton
                                                selectedAssignee={
                                                    createTaskStates[
                                                        record.sectionId
                                                    ]?.assigneeData ||
                                                    createTaskStates[
                                                        record.sectionId
                                                    ]?.assigneeId ||
                                                    null
                                                }
                                                onSelect={(selected) =>
                                                    handleNewTaskAssigneeChange(
                                                        record.sectionId,
                                                        selected
                                                    )
                                                }
                                                projectId={projectId}
                                                filteredAssignees={
                                                    filteredAssignees
                                                }
                                                getAssigneeColor={
                                                    getAssigneeColor
                                                }
                                                getInitials={getInitials}
                                                showName={true}
                                                taskListKind={taskListKind}
                                                open={Boolean(
                                                    newTaskAssigneeOpen[
                                                        record.sectionId
                                                    ]
                                                )}
                                                onOpenChange={(open) =>
                                                    handleNewTaskAssigneeOpenChange(
                                                        record.sectionId,
                                                        open
                                                    )
                                                }
                                                onTabNext={() =>
                                                    handleNewTaskAssigneeTabNext(
                                                        record.sectionId
                                                    )
                                                }
                                            />
                                        </div>
                                        <div
                                            className={styles.taskDateCell}
                                            data-ignore-click
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <DateRangePicker
                                                value={{
                                                    start:
                                                        createTaskStates[
                                                            record.sectionId
                                                        ]?.start || null,
                                                    end:
                                                        createTaskStates[
                                                            record.sectionId
                                                        ]?.end || null,
                                                }}
                                                onChange={(dateRange) =>
                                                    handleNewTaskDateChange(
                                                        record.sectionId,
                                                        dateRange
                                                    )
                                                }
                                                placeholder=""
                                                parentComponent="taskListView"
                                                open={Boolean(
                                                    newTaskDateOpen[
                                                        record.sectionId
                                                    ]
                                                )}
                                                onOpenChange={(open) =>
                                                    handleNewTaskDateOpenChange(
                                                        record.sectionId,
                                                        open
                                                    )
                                                }
                                            />
                                        </div>
                                    </div>
                                )

                                return (
                                    <div className={styles.expandedRowContent}>
                                        {/* Render new task row at top if position is 'top' */}
                                        {isCreatingTask &&
                                            taskPosition === 'top' &&
                                            renderNewTaskRow()}

                                        {/* Render tasks */}
                                        {tasks.length > 0 && (
                                            <div className={styles.tasksList}>
                                                {tasks.map((taskRecord) => {
                                                    const task = taskRecord.task

                                                    return (
                                                        <TaskContextMenu
                                                            key={taskRecord.key}
                                                            task={task}
                                                            projectId={
                                                                projectId
                                                            }
                                                            taskListKind={
                                                                taskListKind
                                                            }
                                                            taskRouteView="list"
                                                            onTaskClick={
                                                                handleOpenTaskFromList
                                                            }
                                                            refreshTasks={
                                                                refreshTasks
                                                            }
                                                            isTaskCompleted={getTaskCompleted(
                                                                task
                                                            )}
                                                            isTogglingComplete={
                                                                completingTasks[
                                                                    task?._id
                                                                ]?.isToggling
                                                            }
                                                            onToggleComplete={() =>
                                                                handleToggleTaskComplete(
                                                                    task
                                                                )
                                                            }
                                                            onClearCelebration={
                                                                clearCompletionCelebration
                                                            }
                                                        >
                                                            <div
                                                                className={
                                                                    styles.taskRowWrapper
                                                                }
                                                            >
                                                                <div
                                                                    className={
                                                                        styles.taskNameCell
                                                                    }
                                                                    onClick={() =>
                                                                        handleOpenTaskFromList(
                                                                            task?._id
                                                                        )
                                                                    }
                                                                >
                                                                    {taskListKind ===
                                                                        'support' && (
                                                                        <span
                                                                            data-ignore-click
                                                                            onClick={(
                                                                                e
                                                                            ) => {
                                                                                e.stopPropagation()
                                                                                if (
                                                                                    !canEditModule
                                                                                )
                                                                                    return
                                                                                handleToggleTaskComplete(
                                                                                    task
                                                                                )
                                                                            }}
                                                                            onMouseEnter={() => {
                                                                                if (
                                                                                    !canEditModule
                                                                                )
                                                                                    return
                                                                                setCompletingTasks(
                                                                                    (
                                                                                        prev
                                                                                    ) => ({
                                                                                        ...prev,
                                                                                        [task?._id]:
                                                                                            {
                                                                                                ...prev[
                                                                                                    task
                                                                                                        ?._id
                                                                                                ],
                                                                                                isHovered: true,
                                                                                            },
                                                                                    })
                                                                                )
                                                                            }}
                                                                            onMouseLeave={() => {
                                                                                if (
                                                                                    !canEditModule
                                                                                )
                                                                                    return
                                                                                setCompletingTasks(
                                                                                    (
                                                                                        prev
                                                                                    ) => ({
                                                                                        ...prev,
                                                                                        [task?._id]:
                                                                                            {
                                                                                                ...prev[
                                                                                                    task
                                                                                                        ?._id
                                                                                                ],
                                                                                                isHovered: false,
                                                                                            },
                                                                                    })
                                                                                )
                                                                            }}
                                                                            style={{
                                                                                display:
                                                                                    'inline-flex',
                                                                                alignItems:
                                                                                    'center',
                                                                                justifyContent:
                                                                                    'center',
                                                                                width: 18,
                                                                                height: 18,
                                                                                borderRadius:
                                                                                    '50%',
                                                                                border: `1px solid ${
                                                                                    getTaskCompleted(
                                                                                        task
                                                                                    )
                                                                                        ? 'transparent'
                                                                                        : completingTasks[
                                                                                                task
                                                                                                    ?._id
                                                                                            ]
                                                                                                ?.isHovered
                                                                                          ? '#5da182'
                                                                                          : '#cbd5e1'
                                                                                }`,
                                                                                backgroundColor:
                                                                                    getTaskCompleted(
                                                                                        task
                                                                                    )
                                                                                        ? '#5da182'
                                                                                        : completingTasks[
                                                                                                task
                                                                                                    ?._id
                                                                                            ]
                                                                                                ?.isHovered
                                                                                          ? '#f0fdf4'
                                                                                          : 'transparent',
                                                                                cursor:
                                                                                    !canEditModule ||
                                                                                    completingTasks[
                                                                                        task
                                                                                            ?._id
                                                                                    ]
                                                                                        ?.isToggling
                                                                                        ? 'default'
                                                                                        : 'pointer',
                                                                                opacity:
                                                                                    completingTasks[
                                                                                        task
                                                                                            ?._id
                                                                                    ]
                                                                                        ?.isToggling
                                                                                        ? 0.6
                                                                                        : 1,
                                                                                transition:
                                                                                    'all 150ms ease, border-color 150ms ease, background-color 150ms ease',
                                                                                flexShrink: 0,
                                                                            }}
                                                                        >
                                                                            <Icon
                                                                                icon="iconamoon:check-light"
                                                                                width={
                                                                                    13
                                                                                }
                                                                                height={
                                                                                    13
                                                                                }
                                                                                style={{
                                                                                    color: getTaskCompleted(
                                                                                        task
                                                                                    )
                                                                                        ? '#ffffff'
                                                                                        : completingTasks[
                                                                                                task
                                                                                                    ?._id
                                                                                            ]
                                                                                                ?.isHovered
                                                                                          ? '#5da182'
                                                                                          : '#94a3b8',
                                                                                    transition:
                                                                                        'color 150ms ease',
                                                                                }}
                                                                            />
                                                                        </span>
                                                                    )}
                                                                    {editingTasks[
                                                                        task
                                                                            ?._id
                                                                    ]
                                                                        ?.isEditing ? (
                                                                        <Input
                                                                            ref={(
                                                                                el
                                                                            ) => {
                                                                                if (
                                                                                    el
                                                                                ) {
                                                                                    taskInputRefs.current[
                                                                                        task?._id
                                                                                    ] =
                                                                                        el
                                                                                }
                                                                            }}
                                                                            value={
                                                                                editingTasks[
                                                                                    task
                                                                                        ._id
                                                                                ]
                                                                                    ?.editedTitle ||
                                                                                task?.title
                                                                            }
                                                                            onChange={(
                                                                                e
                                                                            ) =>
                                                                                setEditingTasks(
                                                                                    (
                                                                                        prev
                                                                                    ) => ({
                                                                                        ...prev,
                                                                                        [task?._id]:
                                                                                            {
                                                                                                ...prev[
                                                                                                    task
                                                                                                        ._id
                                                                                                ],
                                                                                                editedTitle:
                                                                                                    e
                                                                                                        .target
                                                                                                        .value,
                                                                                            },
                                                                                    })
                                                                                )
                                                                            }
                                                                            onBlur={() =>
                                                                                handleTaskTitleSave(
                                                                                    task
                                                                                )
                                                                            }
                                                                            onKeyDown={(
                                                                                e
                                                                            ) =>
                                                                                handleTaskTitleKeyDown(
                                                                                    e,
                                                                                    task
                                                                                )
                                                                            }
                                                                            className={
                                                                                styles.taskTitleInput
                                                                            }
                                                                            disabled={
                                                                                editingTasks[
                                                                                    task
                                                                                        ._id
                                                                                ]
                                                                                    ?.isUpdating
                                                                            }
                                                                            onClick={(
                                                                                e
                                                                            ) =>
                                                                                e.stopPropagation()
                                                                            }
                                                                            onMouseDown={(
                                                                                e
                                                                            ) =>
                                                                                e.stopPropagation()
                                                                            }
                                                                            bordered={
                                                                                false
                                                                            }
                                                                        />
                                                                    ) : (
                                                                        <span
                                                                            className={
                                                                                styles.taskTitle
                                                                            }
                                                                            onClick={(
                                                                                e
                                                                            ) =>
                                                                                handleTaskTitleEdit(
                                                                                    task?._id,
                                                                                    task?.title,
                                                                                    e
                                                                                )
                                                                            }
                                                                            style={{
                                                                                cursor: 'text',
                                                                            }}
                                                                        >
                                                                            {
                                                                                task?.title
                                                                            }
                                                                        </span>
                                                                    )}
                                                                    {renderTaskSubtaskMeta(
                                                                        task
                                                                    )}
                                                                    {task?.commentsLength >
                                                                        0 && (
                                                                        <Tooltip
                                                                            title="View comments"
                                                                            placement="top"
                                                                        >
                                                                            <div
                                                                                className={
                                                                                    styles.taskMeta
                                                                                }
                                                                                onClick={(
                                                                                    e
                                                                                ) => {
                                                                                    e.stopPropagation()
                                                                                    handleOpenTaskFromList(
                                                                                        task?._id,
                                                                                        'comments'
                                                                                    )
                                                                                }}
                                                                                style={{
                                                                                    cursor: 'pointer',
                                                                                }}
                                                                            >
                                                                                <span
                                                                                    className={
                                                                                        styles.commentCount
                                                                                    }
                                                                                >
                                                                                    {
                                                                                        task?.commentsLength
                                                                                    }
                                                                                </span>
                                                                                <Icon
                                                                                    icon="iconamoon:comment-light"
                                                                                    width={
                                                                                        14
                                                                                    }
                                                                                    height={
                                                                                        14
                                                                                    }
                                                                                    className={
                                                                                        styles.commentIcon
                                                                                    }
                                                                                />
                                                                            </div>
                                                                        </Tooltip>
                                                                    )}
                                                                    {renderTaskTrailing(
                                                                        task
                                                                    )}
                                                                </div>
                                                                <div
                                                                    className={
                                                                        styles.taskAssigneeCell
                                                                    }
                                                                    data-ignore-click
                                                                    onClick={(
                                                                        e
                                                                    ) =>
                                                                        e.stopPropagation()
                                                                    }
                                                                >
                                                                    <AssigneeSelectorButton
                                                                        selectedAssignee={
                                                                            task?.assigneeId
                                                                        }
                                                                        onSelect={
                                                                            canEditModule
                                                                                ? (
                                                                                      selected
                                                                                  ) =>
                                                                                      handleAssigneeChange(
                                                                                          task,
                                                                                          selected
                                                                                      )
                                                                                : undefined
                                                                        }
                                                                        projectId={
                                                                            projectId
                                                                        }
                                                                        filteredAssignees={
                                                                            filteredAssignees
                                                                        }
                                                                        getAssigneeColor={
                                                                            getAssigneeColor
                                                                        }
                                                                        getInitials={
                                                                            getInitials
                                                                        }
                                                                        showName={
                                                                            true
                                                                        }
                                                                        taskListKind={
                                                                            taskListKind
                                                                        }
                                                                    />
                                                                </div>
                                                                <div
                                                                    className={
                                                                        styles.taskDateCell
                                                                    }
                                                                    data-ignore-click
                                                                    onClick={(
                                                                        e
                                                                    ) =>
                                                                        e.stopPropagation()
                                                                    }
                                                                >
                                                                    <DateRangePicker
                                                                        highlightOverdue
                                                                        value={{
                                                                            start:
                                                                                task?.start ||
                                                                                null,
                                                                            end:
                                                                                task?.end ||
                                                                                null,
                                                                        }}
                                                                        onChange={
                                                                            canEditModule
                                                                                ? (
                                                                                      dateRange
                                                                                  ) =>
                                                                                      handleDateRangeChange(
                                                                                          task,
                                                                                          dateRange
                                                                                      )
                                                                                : undefined
                                                                        }
                                                                        placeholder=""
                                                                        parentComponent="taskListView"
                                                                    />
                                                                </div>
                                                            </div>
                                                        </TaskContextMenu>
                                                    )
                                                })}
                                            </div>
                                        )}

                                        {/* Render new task row at bottom if position is 'bottom' */}
                                        {isCreatingTask &&
                                            taskPosition === 'bottom' &&
                                            renderNewTaskRow()}

                                        {/* Show bottom "Add task..." button only when not creating at bottom */}
                                        {canEditModule &&
                                            !(
                                                isCreatingTask &&
                                                taskPosition === 'bottom'
                                            ) && (
                                                <div
                                                    className={`${styles.addTaskButton}${
                                                        isMain
                                                            ? ` ${styles.addTaskButtonSupport}`
                                                            : ''
                                                    }`}
                                                    onClick={() =>
                                                        handleAddTaskClick(
                                                            record.sectionId,
                                                            'bottom'
                                                        )
                                                    }
                                                >
                                                    <Input
                                                        placeholder="Add Task..."
                                                        readOnly
                                                        tabIndex={-1}
                                                        onMouseDown={(e) => {
                                                            // Prevent focus styles / border flicker on the placeholder
                                                            e.preventDefault()
                                                        }}
                                                        className={
                                                            styles.addTaskPlaceholder
                                                        }
                                                    />
                                                </div>
                                            )}

                                        {/* Scrolling this into view loads the
                                            next batch for this section. */}
                                        {hiddenTaskCount > 0 && (
                                            <div
                                                ref={(el) =>
                                                    registerSectionSentinel(
                                                        record.sectionId,
                                                        el
                                                    )
                                                }
                                                className={
                                                    styles.loadMoreSentinel
                                                }
                                            >
                                                <Icon
                                                    icon="line-md:loading-loop"
                                                    width={16}
                                                    height={16}
                                                />
                                                <span>
                                                    Loading {hiddenTaskCount}{' '}
                                                    more…
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                )
                            },
                            indentSize: 0,
                        }}
                        className={styles.taskTable}
                        rowClassName={(record) => {
                            if (record.isSection) {
                                return styles.sectionRow
                            }
                            return styles.taskTableRow
                        }}
                    />
                </div>
                {/* Add Section Button/Input at the end */}
                {/* <div
                    className={styles.addSectionContainer}
                    style={{
                        paddingLeft: isAddingSection ? '20px' : '16px',
                    }}
                >
                    {!isAddingSection ? (
                        <div
                            className={styles.addSectionButton}
                            onClick={handleAddSection}
                        >
                            <Icon
                                icon="mdi:plus"
                                width={18}
                                height={18}
                                style={{
                                    color: '#94a3b8',
                                }}
                            />
                            <span>Add section</span>
                        </div>
                    ) : (
                        <div className={styles.addSectionRowWrapper}>
                            <div className={styles.sectionHeaderRow}>
                                <div className={styles.sectionHeaderLeft}>
                                    <Icon
                                        icon="tabler:caret-right-filled"
                                        width={16}
                                        height={16}
                                        style={{
                                            color: '#94a3b8',
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Input
                                        ref={addSectionInputRef}
                                        value={newSectionName}
                                        onChange={(e) =>
                                            setNewSectionName(e.target.value)
                                        }
                                        onBlur={handleSectionInputBlur}
                                        onKeyDown={handleSectionInputKeyDown}
                                        placeholder="New section"
                                        disabled={isCreatingSection}
                                        className={styles.sectionNameInput}
                                        autoFocus
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                </div>
                                <div className={styles.sectionHeaderRight}>
                                    <Tooltip title="Add task">
                                        <Icon
                                            icon="mdi:plus"
                                            width={20}
                                            height={20}
                                            className={
                                                styles.columnHeaderPlusIcon
                                            }
                                            onClick={(e) => {
                                                e.stopPropagation()
                                            }}
                                            style={{
                                                cursor: 'pointer',
                                                color: '#636363',
                                            }}
                                        />
                                    </Tooltip>
                                    <Popover
                                        content={null}
                                        trigger="click"
                                        placement="bottomRight"
                                        overlayClassName={
                                            styles.sectionMenuPopover
                                        }
                                    >
                                        <Tooltip title="More options">
                                            <Icon
                                                icon="weui:more-outlined"
                                                width={20}
                                                height={20}
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                }}
                                                style={{
                                                    cursor: 'pointer',
                                                    color: '#636363',
                                                }}
                                            />
                                        </Tooltip>
                                    </Popover>
                                </div>
                            </div>
                            <div className={styles.addSectionSeparator}></div>
                            <div className={styles.addTaskPlaceholderWrapper}>
                                <Input
                                    placeholder="Add task..."
                                    readOnly
                                    className={styles.addTaskPlaceholder}
                                />
                            </div>
                        </div>
                    )}
                </div> */}
            </div>
        )
    }

    return (
        <>
            {renderContent()}
            {showCompletionCelebration &&
                celebrationTaskId &&
                typeof document !== 'undefined' &&
                createPortal(
                    <div
                        key={`task-complete-fx-${celebrationTaskId}`}
                        className={styles.taskCompleteUnicornFx}
                        onAnimationEnd={clearCompletionCelebration}
                    >
                        <img
                            className={styles.taskCompleteUnicornSvg}
                            src={unicornFxImage}
                            alt="Task completed celebration"
                            aria-hidden="true"
                        />
                    </div>,
                    document.body
                )}
            <DeleteSectionModal
                open={deleteModalOpen}
                onCancel={handleModalCancel}
                onConfirm={handleConfirmDeleteSection}
                deleteSectionData={deleteSectionData}
                deleteOption={deleteOption}
                setDeleteOption={setDeleteOption}
                confirmLoading={deleteSectionLoading}
            />
        </>
    )
}

export default ProjectTaskListView
